import type { PricePosition } from '../api/types';

/**
 * The validated dark-surface categorical palette, in fixed slot order.
 * Assign by index; never cycle. Forms that compare every pair at once use
 * SERIES_ALL_PAIRS_SAFE, the prefix that also clears the all-pairs gates.
 */
export const SERIES = [
  '#3987e5', // 1 blue
  '#d95926', // 2 orange
  '#199e70', // 3 aqua
  '#c98500', // 4 yellow
  '#d55181', // 5 magenta
  '#008300', // 6 green
  '#9085e9', // 7 violet
  '#e66767', // 8 red
] as const;

export const SERIES_ALL_PAIRS_SAFE = SERIES.slice(0, 3);

/** Diverging: blue = we undercut the market, red = we are above it. */
export const DIVERGE = {
  low: '#3987e5',
  lowSoft: '#1c5cab',
  mid: '#383835',
  highSoft: '#d03b3b',
  high: '#e66767',
} as const;

export const STATUS = {
  good: '#0ca30c',
  warning: '#fab219',
  serious: '#ec835a',
  critical: '#d03b3b',
} as const;

export const CHROME = {
  surface: '#1a1a19',
  grid: '#2c2c2a',
  axis: '#383835',
  ink: '#ffffff',
  inkSecondary: '#c3c2b7',
  inkMuted: '#898781',
} as const;

/**
 * Scan freshness is a state, not a category: the data is usable, past due, or
 * absent. The age ORDER is already carried by the axis, so the fill does not
 * repeat it — which is also what keeps every bar above 3:1 against the dark
 * surface. A darkening ramp cannot: on this surface any step separable from
 * mid-blue falls to 1.5:1, and in a bar chart the height is the measurement, so
 * an invisible bar cannot be relieved by a label.
 */
export const FRESHNESS_COLOR = {
  fresh: '#3987e5',
  overdue: STATUS.critical,
  /* Deliberately the only desaturated fill: never measured, not merely old. */
  missing: CHROME.inkMuted,
} as const;

/**
 * Position is an ordered state, not a free category: cheapest reads as the good
 * pole, expensive as the bad one, so it borrows the diverging scale rather than
 * four unrelated hues. Each is always paired with its label.
 */
export const POSITION_COLOR: Record<PricePosition, string> = {
  cheapest: DIVERGE.low,
  competitive: '#7fb2ee',
  expensive: DIVERGE.high,
  /* Not on the cheap-to-dear scale at all: there is nobody to compare with. */
  soleSeller: SERIES[6],
  unmatched: CHROME.axis,
};

export const POSITION_LABEL: Record<PricePosition, string> = {
  cheapest: 'Cheapest',
  competitive: 'Top 3',
  expensive: 'Undercut',
  soleSeller: 'Only seller',
  unmatched: 'Not on Heureka',
};

export const POSITION_ORDER: PricePosition[] = [
  'cheapest',
  'competitive',
  'expensive',
  'soleSeller',
  'unmatched',
];

export const MATCH_LABEL: Record<string, string> = {
  matched: 'Matched',
  notFound: 'Not found',
  ambiguous: 'Ambiguous',
  failed: 'Failed',
};

export const MATCH_COLOR: Record<string, string> = {
  matched: STATUS.good,
  notFound: CHROME.axis,
  ambiguous: STATUS.warning,
  failed: STATUS.critical,
};

/** Maps a percentage gap onto the diverging scale. */
export function gapColor(gapPct: number | null | undefined): string {
  if (gapPct === null || gapPct === undefined) {
    return CHROME.axis;
  }
  if (gapPct <= -5) {
    return DIVERGE.low;
  }
  if (gapPct < 0) {
    return '#7fb2ee';
  }
  if (gapPct === 0) {
    return CHROME.inkMuted;
  }
  if (gapPct <= 5) {
    return DIVERGE.highSoft;
  }

  return DIVERGE.high;
}
